import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Import packages
import * as Yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// Import CSS
import "../../../theme/style.scss";
import "react-phone-number-input/style.css";

// Import UI Components
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CustomBreadcrumb from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../../components/CustomFields/TextField";

// Import services
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import { PreferencesContext } from "../../../PreferenceContext";
// import {
//   CreateRefOBGyExaminationFetalHeartRateDescription,
//  GetRefOBGyExaminationFetalHeartRateDescriptionDetail,
//  UpdateRefOBGyExaminationFetalHeartRateDescription,
// } from "../../../services/RefPatientIllnessItemService";
import { CreateRefOBGyExaminationFetalHeartRateDescription, UpdateRefOBGyExaminationFetalHeartRateDescription, GetRefOBGyExaminationFetalHeartRateDescriptionDetail } from "../../../services/RefOBGyExaminationFetalHeartRateDescriptionService";

// Schema for the form
interface formInitialValueTypes {
  refOBGyExaminationFetalHeartRateDescriptionId: number;
  consumerId: number;
  organizationId: number;
  branchId: number;
  departmentId: number;
  specialityId: number;
  fetalHeartRateDescriptionName: string;
  code: string;
  isLocked: boolean;
  isDeleted: boolean;
}

export default function RefOBGyExaminationFetalHeartRateDescriptionSave(props: any) {
  const { isForPopUp = false, handleClosePopUp, name } = props;

  // Initial schema value for form
  const initlRefFetalHeartRateDescriptionData: formInitialValueTypes = {
    consumerId: 0,
    organizationId: 0,
    branchId: 0,
    departmentId: 0,
    specialityId: 0,
    isDeleted: false,
    refOBGyExaminationFetalHeartRateDescriptionId: 0,
    fetalHeartRateDescriptionName: isForPopUp ? name?.toUpperCase() : "",
    isLocked: false,
    code: "",
  };

  // Hooks and context
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);

  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // Getting params from URL
  let urlParams = useQuery();
  let curRawRefFetalHeartRateDescriptionId = urlParams.get("q");
  let curRefFetalHeartRateDescriptionId = 0;

  if (curRawRefFetalHeartRateDescriptionId !== null &&
    curRawRefFetalHeartRateDescriptionId !== undefined &&
    !isForPopUp
  ) {
    let decId = edService.decrypt(curRawRefFetalHeartRateDescriptionId);
    if (decId) {
      curRefFetalHeartRateDescriptionId = +decId;
    }
  }

  // States for loading and error handling
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  // Schema validation
  const validationSchema = Yup.object().shape({
    refOBGyExaminationFetalHeartRateDescriptionId: Yup.number().nullable(),
    consumerId: Yup.number().required(),
    organizationId: Yup.number().required(),
    branchId: Yup.number().required(),
    departmentId: Yup.number().required(),
    specialityId: Yup.number().required(),
    fetalHeartRateDescriptionName: Yup.string().required("Fetal Heart Rate Description Name is required"),
    code: Yup.string(),
    isLocked: Yup.boolean(),
    isDeleted: Yup.boolean(),
  });

  // Form hook
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: initlRefFetalHeartRateDescriptionData,
  });

  // Fetch Ref Breast Examination detail
  const getRefFetalHeartRateDescriptionDetail = async () => {
    let params = new URLSearchParams();
    params.append("refOBGyExaminationFetalHeartRateDescriptionId", curRefFetalHeartRateDescriptionId?.toString());
    params.append("consumerId", userContext?.consumerId.toString());
    params.append("organizationId", userContext?.organizationId.toString());
    params.append("branchId", userContext?.branchId.toString());
    params.append("departmentId", userContext?.departmentId.toString());
    params.append("specialityId", userContext?.specialityId.toString());
    try {
      const res: any = await GetRefOBGyExaminationFetalHeartRateDescriptionDetail(params);
      const FetalHeartRateDescriptionItem = res.data.data;
      methods.reset({
        ...initlRefFetalHeartRateDescriptionData,
        refOBGyExaminationFetalHeartRateDescriptionId: FetalHeartRateDescriptionItem?.refOBGyExaminationFetalHeartRateDescriptionId || 0,
        code: FetalHeartRateDescriptionItem?.code || "",
        fetalHeartRateDescriptionName: FetalHeartRateDescriptionItem?.fetalHeartRateDescriptionName || "",
      });
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? e : e?.message,
        alertType: AlertType.Error,
      });
    }
  };

  // Save and update RefPatientIllnessItem
  const saveRefFetalHeartRateDescription = async (value: formInitialValueTypes) => {
    let result: any = "";
    let formData = {
      refOBGyExaminationFetalHeartRateDescriptionId: value.refOBGyExaminationFetalHeartRateDescriptionId,
      specialityId: userContext?.specialityId,
      departmentId: userContext?.departmentId,
      branchId: userContext?.branchId,
      organizationId: userContext?.organizationId,
      consumerId: userContext?.consumerId,
      fetalHeartRateDescriptionName: value?.fetalHeartRateDescriptionName?.trim() || "",
      code: value.code?.trim(),
      isLocked: value.isLocked,
      isDeleted: value.isDeleted,
      DataManagedBy: userContext.loginDetail.userId,
    };
    try {
      if (curRefFetalHeartRateDescriptionId > 0) {
        result = await UpdateRefOBGyExaminationFetalHeartRateDescription(formData);
      } else {
        result = await CreateRefOBGyExaminationFetalHeartRateDescription(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          handleClosePopUp("CREATE", result?.data);
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? e : e?.message,
        alertType: AlertType.Error,
      });
    }
  };

  // On form submit
  const onSubmit = async (data: any) => {
    saveRefFetalHeartRateDescription(data);
  };

  // Handle back action
  const handleBack = () => {
    if (!isForPopUp) {
      navigate(-1);
    } else {
      handleClosePopUp("CLOSE");
    }
  };

  // Fetch data when component mounts or ref Breast Examination id changes
  const fetchData = async () => {
    if (curRefFetalHeartRateDescriptionId > 0) {
      await getRefFetalHeartRateDescriptionDetail();
    }
    setIsLoading(false);
  };

  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curRefFetalHeartRateDescriptionId]);

  // Handle skeleton visibility
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50);
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box>
      <Box className={"main-container"}>
        {isForPopUp === false && (
          <Grid container className="page-header">
            <Grid item xs={6}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CustomBreadcrumb />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={handleBack}
                  startIcon={<ArrowBackIcon />}
                >
                  Back
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Grid container gap={2}>
              <Grid item xs={12} className={"group-container"}>
                <Grid item container xs={12} spacing={2}>
                  <Grid item xs={12} sm={isForPopUp ? 12 : 6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.fetalHeartRateDescriptionName ? "validation-message" : ""}`}
                      >
                        Breast Examination *
                      </FormLabel>
                      <SkeletonWrapper isLoading={showSkeleton} className="skeleton-input">
                        <TextField
                          type="text"
                          fullWidth
                          size="small"
                          autoFocus
                          {...methods.register("fetalHeartRateDescriptionName", {
                            onChange: (e) => {
                              const input = e.target;
                              const selectionStart = input.selectionStart;
                              const selectionEnd = input.selectionEnd;

                              // CONVERT INPUT INTO UPPERCASE LATTERS
                              e.target.value = input.value
                                ? input.value.toUpperCase()
                                : "";

                              // Restore the cursor position
                              setTimeout(() => {
                                input.setSelectionRange(
                                  selectionStart,
                                  selectionEnd
                                );
                              });

                              methods.setValue(
                                "fetalHeartRateDescriptionName",
                                e.target.value,
                                {
                                  shouldValidate: true,
                                }
                              ); // UPDATE VALUE
                            },
                            required: true,
                          })}
                          error={!!methods.formState.errors.fetalHeartRateDescriptionName}
                          className={`text-input ${methods.formState.errors.fetalHeartRateDescriptionName ? "error" : ""}`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.fetalHeartRateDescriptionName?.message}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    variant="contained"
                    type="submit"
                    name="save"
                    disabled={showSkeleton}
                  >
                    {curRefFetalHeartRateDescriptionId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    variant="outlined"
                    type="button"
                    name="cancel"
                    onClick={handleBack}
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
}
